import { ImageServiceType } from '@omni/kit/Types';
import { KitListItem, KitText } from '@omni/kit/components';
import { EventImage } from '@omni/kit/components/EventImage';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import { IGroupEvent } from '@omni/kit/services/GroupsService/Types';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { dateFormatRange } from '@omni/kit/utilities/dateFormatRange';
import { createImageUrl, parseImageUrl } from '@omni/kit/utilities/utilities';
import {
  NavigationProp,
  ParamListBase,
  useNavigation,
} from '@react-navigation/native';
import React, { ReactElement } from 'react';
import { Pressable, StyleSheet } from 'react-native';

import * as Constants from '../../../Constants';

interface EventsListItemsProps {
  accessToken: string | null | undefined;
  events: IGroupEvent[];
  openListEventModal: () => void;
}

export default function GroupEventListItems({
  accessToken,
  events,
  openListEventModal,
}: EventsListItemsProps): ReactElement {
  const { t } = useTranslation();
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }

  const displayedEvents = events.slice(0, 2);

  return (
    <>
      {displayedEvents?.map((event, index) => {
        const imgUrl = parseImageUrl(
          createImageUrl(event?._embedded?.images?.[0].id),
          36,
          36,
          ImageServiceType.ImageJpeg
        );

        return (
          <KitListItem
            key={`${event.id}-${index}`}
            data-testid={event.title}
            title={event.title}
            subtitle={dateFormatRange({
              startDate: event.start_at ? event.start_at : '',
              endDate: event.end_at,
              timezone: event.timezone,
              allDay: event?.all_day,
            })}
            titleColor={Colors.N900}
            titleFontSize={16}
            ImageComponent={
              <EventImage
                url={imgUrl}
                date={event.start_at}
                timezone={event.timezone}
              />
            }
            minHeight={80}
            showRightIconOnAndroid={true}
            iconColor={Colors.N300}
            onPress={() =>
              navigation.navigate(Constants.EVENT_DETAILS_SCREEN, {
                eventId: event.id,
              })
            }
          />
        );
      })}
      <Pressable
        onPress={openListEventModal}
        style={({ pressed }) => [
          styles.viewGroupEvents,
          {
            backgroundColor: pressed ? Colors.N50 : Colors.N0,
          },
        ]}
      >
        <KitText semiBold black>
          {t('groups:viewAllGroupEvents')}
        </KitText>
      </Pressable>
    </>
  );
}

const styles = StyleSheet.create({
  viewGroupEvents: {
    paddingVertical: Spacing.l,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
